"use client";

import { countryNamesByAlpha2Code } from "@/common/utilities/geography/countryNames";
import SearchField from "@/component-library/components/user-input/search-field/SearchField";
import React, { useMemo, useState } from "react";

// Same source as CountrySelect: the full ISO 3166-1 list, keyed by alpha-2 code.
const allCountries = countryNamesByAlpha2Code;

const COUNTRIES = Object.entries(allCountries)
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

// The list is ~250 entries; showing a handful keeps the dropdown usable and
// avoids re-rendering every country on each keystroke.
const MAX_RESULTS = 8;

interface SearchableCountrySelectProps {
    name: string;
    /** Selected ISO 3166-1 alpha-2 code, or undefined when nothing is chosen. */
    value?: string;
    onValueChange: (countryCode: string | undefined) => void;
    placeholder?: string;
    descriptionForScreenReaders: string;
}

/**
 * Country picker with type-ahead search, for cases where CountrySelect's native
 * <select> over ~250 entries is unwieldy.
 *
 * The visible field holds the country NAME while the caller receives the alpha-2
 * CODE. Editing the text after choosing clears the selection, so a half-typed
 * query can never submit a stale code — callers should therefore treat an
 * undefined value as "no country chosen" rather than reading the input text.
 */
export const SearchableCountrySelect: React.FC<
    SearchableCountrySelectProps
> = ({
    name,
    value,
    onValueChange,
    placeholder = "Search for a country",
    descriptionForScreenReaders,
}) => {
    const [query, setQuery] = useState(value ? (allCountries[value] ?? "") : "");

    const dropdownItems = useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        // Nothing typed means no suggestions: the alphabetical head of a ~250
        // entry list is noise, not a useful default, and it covers the field the
        // moment it takes focus.
        if (!trimmed) return [];

        return COUNTRIES.filter(
            (country) =>
                country.name.toLowerCase().includes(trimmed) ||
                country.code.toLowerCase() === trimmed
        )
            .slice(0, MAX_RESULTS)
            .map((country) => ({ id: country.code, label: country.name }));
    }, [query]);

    return (
        <SearchField
            name={name}
            value={query}
            placeholder={placeholder}
            descriptionForScreenReaders={descriptionForScreenReaders}
            dropdownItems={dropdownItems}
            onChange={(newQuery) => {
                setQuery(newQuery);
                // Any edit invalidates a previous pick: the text no longer
                // necessarily names the selected country.
                if (value) {
                    onValueChange(undefined);
                }
            }}
            onSelection={(result) => {
                if (!result) return;
                setQuery(result.label);
                onValueChange(result.id);
            }}
        />
    );
};

export default SearchableCountrySelect;
